// =================================================================================================
// NAV-2 — **THE BOTTOM BAR CHECKS ITS OWN POSITION AND PUTS ITSELF BACK.**
//
// HER ORDER, third occurrence, and her word is that this round is the last: *"the bar checks its own
// bounding position on a slow heartbeat and on visibility/resume events, and if it ever finds itself
// unstuck it restores itself instantly — so even an unknown future cause can never strand it for
// more than a beat."* Her belt-and-suspenders, in her own words, and it is deliberately **CAUSE-
// AGNOSTIC**: it does not care why the bar drifted.
//
// >>> WHY THE ARMOR IS THE ROUND RATHER THAN A FIX AT A NAMED CAUSE, MEASURED BEFORE A LINE WAS
// WRITTEN (R25/R180). <<< The hunt her clause 1 asks for was run against every suspect she named —
// a keyboard-shaped visual-viewport shrink and restore, backgrounding and resuming, an overlay that
// locks the body, an overlay that LEAVES a body style behind, and a transform landing on an
// ancestor — and **Chromium reproduced NONE of them: fourteen states, fourteen PINNED.** More than
// that, the containing-block class NAV-1 chased is now structurally impossible: L4 portalled this
// bar to `<body>`, so a transform on `main` is not in its ancestor chain at all, which the hunt
// confirmed by finding zero captors while one was live.
//
// **SO THE REMAINING SUSPECT IS THE ONE NAV-1 NAMED AND DECLINED TO TOUCH ON SPECULATION: WEBKIT'S
// OWN FIXED-ELEMENT BEHAVIOUR, WHICH NO INSTRUMENT IN THIS REPO CAN REACH.** Her diagnostic gold
// says the same thing from the other end — it unsticks DURING a session and only a full close-and-
// reopen fixes it, which is a rendering state rather than a style anybody wrote. **A watchdog is the
// only repair that works against a cause this house cannot name**, and it is exactly what she asked
// for.
//
// It imports NOTHING, which is the entry requirement that lets `scripts/nav2-self-heal.mjs` LOAD and
// RUN every line of it with no browser (R146: reach is transitive). The window, the element and the
// clock are all PARAMETERS (R46's locked-door shape), so a gate drives a drift with no phone.
// =================================================================================================

/** How far the bar may sit from the viewport foot before it counts as unstuck. */
export const NAV_PIN_TOLERANCE_PX = 2;

/**
 * The heartbeat. **SLOW IS HER OWN WORD**, and it is a real constraint rather than a preference: a
 * check that runs every frame is a layout read every frame on the one surface a member scrolls with
 * her thumb, and reading `getBoundingClientRect` forces a synchronous layout (PERF-1's whole
 * finding, one component over). Two seconds is imperceptible to her and free to the browser.
 */
export const NAV_PIN_HEARTBEAT_MS = 2000;

/**
 * >>> IS IT UNSTUCK? THREE ANSWERS, NEVER TWO (R18), AND THE THIRD IS WHAT KEEPS THIS SAFE. <<<
 *
 * `pinned` and `unstuck` are the obvious pair. **`unknown` is the one a naive build loses**: a bar
 * that is `display: none` (every desktop width — this bar is a phone surface), one whose rect has
 * collapsed to nothing, or a viewport the browser has not reported yet all measure as *not at the
 * foot*. Read as `unstuck`, the watchdog would fire forever on every desktop page in the house,
 * re-pinning a bar nobody is looking at — a heartbeat that never rests, with no symptom but a
 * browser doing work (R8-4's silent direction).
 */
export function navPinState(rect, viewportHeight, { tolerance = NAV_PIN_TOLERANCE_PX } = {}) {
  if (!rect || typeof rect !== "object") return "unknown";
  // >>> PRESENCE, NEVER COERCION (R53/R136/R137). <<< `Number("852")` is 852 and `Number(true)`
  // is 1, so a rect-shaped object carrying STRINGS -- which is what a serialised or hand-built rect
  // looks like -- would be read as a real measurement and could pass a verdict about a bar nobody
  // measured. Only a real number is a coordinate.
  const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const bottom = num(rect.bottom);
  const height = num(rect.height);
  const vh = num(viewportHeight);
  if (bottom === null || height === null || vh === null) return "unknown";
  // A bar with no box is not a drifted bar — it is a bar that is not on this screen.
  if (height <= 0 || vh <= 0) return "unknown";
  return Math.abs(bottom - vh) <= tolerance ? "pinned" : "unstuck";
}

/**
 * ================================================================================================
 * >>> **THE VISUAL VIEWPORT'S BOTTOM, IN LAYOUT COORDINATES — THE FACT THIS FILE WAS BLIND TO.** <<<
 *
 * **MEASURED 2026-08-27, AND IT IS WHY HER BAR KEPT UNSTICKING THROUGH A WATCHDOG BUILT TO CATCH
 * IT.** `navPinState` compared `rect.bottom` against `win.innerHeight`. Both of those are the
 * **LAYOUT** viewport — `getBoundingClientRect()` is layout-relative and so is `innerHeight` — so
 * for a bar the sheet pins with `bottom: 0` they **always agree**, the verdict is always `pinned`,
 * and the heartbeat never fires. *The instrument was blind in exactly the way the class it measures
 * is blind*, which is this house's most-repeated instrument defect.
 *
 * **AND THAT IS THE ONE FAILURE A MOBILE BROWSER REALLY HAS.** On iOS Safari the URL bar collapses
 * and the keyboard opens WITHOUT changing the layout viewport: `position: fixed; bottom: 0` keeps
 * sitting at the foot of a layout viewport the member can no longer entirely see, so her bar slides
 * off the bottom of the glass or floats mid-screen. A headless browser at a phone SIZE does not
 * reproduce it — this walk found the bar planted in all ten rooms with zero drift — which is exactly
 * why it survived every rendered proof this house has taken of it.
 *
 * `offsetTop` is how far the visual viewport has slid DOWN inside the layout one; adding it to the
 * visual height gives the visible bottom edge in the same coordinates a rect is measured in.
 *
 * **NULL WHEN THE BROWSER HAS NO ANSWER**, never a guess: no `visualViewport` (every desktop and
 * older mobile engine), or numbers that are not numbers. A caller reading null falls back to the
 * layout measure, which is what this file did before and is right where there is nothing better.
 * ================================================================================================
 */
export function visualViewportBottom(win) {
  const vv = win && typeof win === "object" ? win.visualViewport : null;
  if (!vv || typeof vv !== "object") return null;
  // PRESENCE, NEVER COERCION (R53/R136/R137) — the same rule the rect reader below already keeps.
  const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const height = num(vv.height);
  const offset = num(vv.offsetTop);
  if (height === null || offset === null || height <= 0) return null;
  return height + offset;
}

/**
 * >>> **HOW FAR THE BAR MUST MOVE TO MEET THE GLASS, AND WHY IT IS A SEPARATE FACT (R18).** <<<
 *
 * `healNavPin` below repairs a bar whose POSITION was stolen — an overlay that set an inline style
 * and did not put it back. That is a different defect from this one, and the same repair cannot
 * serve both: a reflow cannot move a bar that is already correctly pinned to the layout viewport.
 *
 * **THIS WRITES A CORRECTION AND NEVER A COORDINATE**, which is what keeps `healNavPin`'s own rule
 * intact — *the stylesheet owns this bar's position*. The sheet still says `bottom: 0`; all this
 * says is how far the glass has slid away from the layout, which is a fact no stylesheet can
 * express. A bar already meeting the glass answers 0 and nothing is written.
 */
export function navPinShift(rect, visualBottom, { tolerance = NAV_PIN_TOLERANCE_PX } = {}) {
  const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const bottom = rect && typeof rect === "object" ? num(rect.bottom) : null;
  const glass = num(visualBottom);
  if (bottom === null || glass === null) return 0;
  const gap = bottom - glass;
  // Under the tolerance it is the same edge, and writing a sub-pixel transform every heartbeat is a
  // repaint nobody asked for.
  if (Math.abs(gap) <= tolerance) return 0;
  return gap;
}

/**
 * Put the bar back on the glass. Additive to the sheet's own `bottom: 0`, and CLEARED rather than
 * set to zero when there is nothing to correct, so a browser with no visual viewport is left exactly
 * as the stylesheet drew it.
 */
export function applyNavPinShift(el, shift) {
  if (!el || typeof el !== "object" || !el.style) return false;
  if (typeof shift !== "number" || !Number.isFinite(shift) || shift === 0) {
    el.style.transform = "";
    return false;
  }
  el.style.transform = `translateY(${-shift}px)`;
  return true;
}

/** Only one of the three is a reason to act. Asked as its own question so no caller can invert it. */
export function navPinNeedsHeal(state) {
  return state === "unstuck";
}

/**
 * >>> THE REPAIR IS A FORCED RE-LAYOUT OF THE ELEMENT ITSELF, AND IT WRITES NO GEOMETRY. <<<
 *
 * The bar's position is the STYLESHEET's — `position: fixed; left: 0; right: 0; bottom: 0` — and a
 * watchdog that set coordinates of its own would be a second answer to where the bar goes (R28/R29),
 * wrong at the first safe-area or breakpoint change and invisible until somebody photographed it.
 * What this does instead is take the element out of the fixed flow for one synchronous reflow and
 * put it straight back, which is what makes an engine re-place a fixed box it is rendering stale.
 *
 * >>> IT CLEARS ANY INLINE `position` RATHER THAN PUTTING IT BACK, AND THE RENDERED PROOF IS WHAT
 * FOUND THAT. <<< The first draft of this law restored whatever it found, on the reasoning that a
 * heal must not delete somebody else's inline style — and `l4-nav-journey.mjs` drifted the real bar
 * by setting `position: absolute` on it, gave the watchdog three heartbeats, and **the bar stayed
 * unstuck**: the heal set `static`, reflowed, and dutifully put `absolute` straight back. **The
 * preservation rule defeated the repair on exactly the case her own third named suspect describes**
 * — an overlay that toggles a style and fails to restore it.
 *
 * **THE STYLESHEET OWNS THIS BAR'S POSITION** (`position: fixed`, in `body.m3-shell
 * .mobile-tab-bar`), so an inline one on this element is never legitimate: it is either the drift
 * itself or a leftover. Clearing it is what restores her bar, and it is the only thing this writes.
 */
export function healNavPin(el) {
  if (!el || typeof el !== "object" || !el.style) return false;
  el.style.position = "static";
  // The read is the point: it forces the synchronous layout that makes the engine let go of the
  // stale box. Its VALUE is deliberately unused, and it may not be removed as dead code.
  void el.offsetHeight;
  // Cleared, never restored — so the sheet's own `fixed` applies again whatever was standing here.
  el.style.position = "";
  return true;
}

/**
 * The whole watchdog, with every moving part handed in: the element, the window, the clock and the
 * healer. A gate drives a real drift, a real heal and a real teardown with no browser at all.
 *
 * **HER THREE MOMENTS, AND THEY ARE THREE FACTS (R18):** a slow heartbeat catches a drift that
 * happened while she was reading; `visibilitychange` catches the resume her own diagnostic points at
 * (*only a full app close-and-reopen fixes it*); and a viewport change catches the keyboard, which
 * is her first named suspect. One of the three alone leaves a window where the bar is stranded and
 * nothing is looking.
 */
export function watchNavPin({
  win,
  find,
  heal = healNavPin,
  state = navPinState,
  heartbeatMs = NAV_PIN_HEARTBEAT_MS,
} = {}) {
  if (!win || typeof win.setInterval !== "function" || typeof find !== "function") return () => {};
  let healed = 0;
  let shifted = 0;
  const check = () => {
    const el = find();
    if (!el || typeof el.getBoundingClientRect !== "function") return;
    // >>> **THE STOLEN-POSITION CASE FIRST, MEASURED AGAINST THE LAYOUT VIEWPORT AS IT ALWAYS
    // WAS.** <<< An inline `position` left behind by an overlay is a real defect and this is the
    // measure that sees it; the correction below would only slide a bar that is already wrong.
    // The transform is cleared BEFORE measuring, or last beat's correction reads as this beat's
    // drift and the two laws chase each other.
    applyNavPinShift(el, 0);
    const verdict = state(el.getBoundingClientRect(), win.innerHeight);
    if (navPinNeedsHeal(verdict) && heal(el)) healed += 1;
    // >>> **AND THEN THE GLASS, WHICH IS THE HALF THIS FILE COULD NOT SEE UNTIL 2026-08-27.** <<<
    // `null` on any browser without a visual viewport, and a `null` writes nothing at all — so
    // every desktop and every older engine is left exactly as the stylesheet drew it.
    const glass = visualViewportBottom(win);
    if (glass === null) return;
    if (applyNavPinShift(el, navPinShift(el.getBoundingClientRect(), glass))) shifted += 1;
  };

  const timer = win.setInterval(check, heartbeatMs);
  const doc = win.document;
  // >>> EVERY LISTENER IS REMOVED ON TEARDOWN, WHICH IS NOT A FORMALITY HERE. <<< A watchdog that
  // outlives its mount is a second heartbeat, and a second one is a third the next time — this house
  // has paid for exactly that shape (a listener that outlives its mount fires once per mount the
  // component has ever had).
  const offs = [];
  const on = (target, type, fn) => {
    if (!target || typeof target.addEventListener !== "function") return;
    target.addEventListener(type, fn);
    offs.push(() => target.removeEventListener(type, fn));
  };
  on(doc, "visibilitychange", check);
  on(win, "pageshow", check);
  on(win, "resize", check);
  on(win.visualViewport, "resize", check);
  on(win.visualViewport, "scroll", check);

  const stop = () => {
    win.clearInterval(timer);
    for (const off of offs) off();
    offs.length = 0;
    // The correction is this watchdog's own and leaves with it: a bar carrying a translate from a
    // watchdog that is gone is a bar nothing will ever put back (the mount/unmount shape this file
    // already refuses for its listeners).
    const last = find();
    if (last) applyNavPinShift(last, 0);
  };
  stop.healed = () => healed;
  stop.shifted = () => shifted;
  stop.check = check;
  return stop;
}
